import { Info } from "@phosphor-icons/react";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import type { Tone } from "@/tokens";

export type InfoCardProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  tone?: Tone;
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
};

const toneClassName: Record<Tone, string> = {
  neutral: "bfrs-bg-surface-muted bfrs-text-text-secondary",
  primary: "bfrs-bg-primary-soft bfrs-text-primary",
  brand: "bfrs-bg-primary-soft bfrs-text-primary",
  success: "bfrs-bg-success-soft bfrs-text-success-contrast",
  warning: "bfrs-bg-warning-soft bfrs-text-warning-contrast",
  danger: "bfrs-bg-danger-soft bfrs-text-danger-contrast",
  info: "bfrs-bg-info-soft bfrs-text-info-contrast"
};

export const InfoCard = forwardRef<HTMLDivElement, InfoCardProps>(({ className, tone = "info", icon, title, description, actions, children, ...props }, ref) => (
  <div ref={ref} className={cn("bfrs-flex bfrs-items-start bfrs-gap-3 bfrs-rounded-xl bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-p-4", className)} {...props}>
    <span className={cn("bfrs-flex bfrs-size-9 bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-rounded-lg", toneClassName[tone])}>{icon ?? <Info size={18} weight="bold" />}</span>
    <span className="bfrs-min-w-0 bfrs-flex-1">
      <span className="bfrs-block bfrs-text-sm bfrs-font-semibold bfrs-text-text-primary">{title}</span>
      {description && <span className="bfrs-mt-1 bfrs-block bfrs-text-xs bfrs-leading-5 bfrs-text-text-secondary">{description}</span>}
      {children}
    </span>
    {actions && <span className="bfrs-shrink-0">{actions}</span>}
  </div>
));

InfoCard.displayName = "InfoCard";
